/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2023
 * @flow
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  ArcElement,
  PointElement,
  LineElement,
  LineController,
  BarController,
  Legend,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { connect } from 'react-redux';
import { getPopupConfig } from '../../components/Popup';
import FireboardUtils from '../../util/fireboard-util';
import View from './views';
import withSocketContext from '../../context-api/SocketContext/withSocketContext';
import CommonUtil from '../Fireboard/common';
import { getHoursAndMins, range } from './utils';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  BarController,
  ChartDataLabels,
  Tooltip,
  Legend,
  Title,
);

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      popup: undefined,
      items: new Map(),
      categories: new Map(),
      commonData: {
        totalCheck: 0,
        totalAmount: 0,
        subTotalAmount: 0,
        totalGuest: 0,
        totalCategory: 0,
        totalItem: 0,
        totalPaidAmount: 0,
        totalTipAmount: 0,
        totalTipPercent: 0,
        totalUnpaidAmount: 0,
        totalPaidCheck: 0,
        totalUnpaidCheck: 0,
      },
      orderTypesData: new Map(),
      revenueCentersData: new Map(),
      hourlySales: new Map(),
      paymentByTenderType: new Map(),
    };
    this.currency = '$';
    this.checks = new Map();
    this.revenueCentersMap = new Map(); //new Map()<revenueCenterId, revenueCenterName>
    this.orderTypesMap = new Map(); //new Map()<orderTypeId, orderTypeName>
  }

  componentDidMount() {
    const { history, reduxProps: { storeInfo, currencyInfo } } = this.props;
    if (!storeInfo?.data) {
      history.push({
        pathname: '/',
        state: { deviceTypeId: 15 }
      });
      return;
    }

    const { revenueCenters, orderTypes } = storeInfo?.data || {};
    revenueCenters.forEach(rCenter => this.revenueCentersMap.set(rCenter.id, rCenter.name));
    orderTypes.forEach(orderType => this.orderTypesMap.set(orderType.id, orderType.name));
    this.currency = currencyInfo?.currency;

    const { context } = this.props;
    this.setChecks(context?.checks, context?.newChecks);
    context?.setIsDashboardMounted && context.setIsDashboardMounted(true);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (this.props?.context !== newProps?.context) {
      const { context: nextContext } = newProps;
      this.setChecks(nextContext?.checks, nextContext?.newChecks);
    }
  }

  componentWillUnmount() {
    const { context } = this.props;
    context?.setIsDashboardMounted && context.setIsDashboardMounted(false);
  }

  setChecks = (checks, newChecks) => {
    if (newChecks && Array.from(newChecks.values()).length) {
      let newChecksMap = new Map();
      const newChecksArr = Array.from(newChecks.values());
      newChecksArr && newChecksArr.forEach(check => newChecksMap.set(check.id, CommonUtil.parseCheckFromRedis(check)));
      this.calculationForCheckSubscription(newChecksMap);
    } else {
      const allChecks = Array.from(checks.values());
      let allChecksMap = new Map();
      allChecks && allChecks.forEach(check => {
        allChecksMap.set(check.id, CommonUtil.parseCheckFromRedis(check));
      });
      this.checks = allChecksMap;
      this.calculationForAllChecks();
    }
  };

  handleErrorPopup = error => {
    const errorMsg = error.response
      ? error.response.data
      : error.message;
    FireboardUtils.addNetworkLogs(error.response);
    this.getPopup("error", null, errorMsg);
  };

  getPopup = (type, heading, message, callback) => {
    const popup =
      type === "spinner"
        ? getPopupConfig("spinner")
        : getPopupConfig("error", () => {
          callback && callback();
          this.setState({ popup: undefined });
        });
    if (heading) {
      popup.heading = heading;
    }
    if (message) {
      popup.message = message;
    }
    this.setState({ popup });
  };

  onClosePopup = () => {
    this.setState({ popup: undefined });
  };

  calculationForAllChecks = () => {
    const { reduxProps: { storeInfo } } = this.props;
    const { suggestedTipIncludesTax } = storeInfo?.data?.tipInfo || {};
    const allChecks = Array.from(this.checks.values());
    let totalAmt = 0, totalGuest = 0, totalPaidAmt = 0, totalTipAmt = 0, totalUnpaidAmt = 0, totalPaidCheck = 0, subTotalAmt = 0;
    let categoriesMap = new Map(); //new Map()<categoryId, Boolean>
    let menuItemsMap = new Map(); //new Map()<menuItemId, Object>
    let checksByTimeSlot = new Map(); //new Map()<hour, Array of Checks>
    let checksByOrderType = new Map(); //new Map()<orderTypeId, Array of Checks>
    let checksByRevenueCenter = new Map(); //new Map()<revenueCenterId, Array of Checks>
    let paymentByTenderType = new Map();

    allChecks.forEach(check => {
      const { totalAmount, guestCount, paidAmount, tipAmount, unpaidAmount, subTotalAmount, items, payments, sourceType } = check;
      totalAmt += totalAmount;
      totalGuest += guestCount;
      totalPaidAmt += paidAmount;
      totalTipAmt += tipAmount;
      totalUnpaidAmt += unpaidAmount;
      totalPaidCheck += (totalAmount > 0 && unpaidAmount === 0) ? 1 : 0;
      subTotalAmt += subTotalAmount;

      //checks by revenue center
      if (checksByRevenueCenter.has(check.revenueCenterId)) {
        checksByRevenueCenter.set(check.revenueCenterId, [...checksByRevenueCenter.get(check.revenueCenterId), check]);
      } else {
        checksByRevenueCenter.set(check.revenueCenterId, [check]);
      }

      //checks by order type
      if (checksByOrderType.has(check.orderTypeId)) {
        checksByOrderType.set(check.orderTypeId, [...checksByOrderType.get(check.orderTypeId), check]);
      } else {
        checksByOrderType.set(check.orderTypeId, [check]);
      }

      //hourly sales
      const { hours } = getHoursAndMins(check?.openDate);
      if (checksByTimeSlot.has(hours)) {
        checksByTimeSlot.set(hours, [...checksByTimeSlot.get(hours), check]);
      } else {
        checksByTimeSlot.set(hours, [check]);
      }

      items.forEach(item => {
        if (categoriesMap.has(item.categoryId)) {
          categoriesMap.set(item.categoryId, categoriesMap.get(item.categoryId) + 1);
        } else {
          categoriesMap.set(item.categoryId, 1);
        }

        const isSourceType0 = sourceType === 0;
        if (menuItemsMap.has(item.menuItemId)) {
          //update quantity
          const menuItem = menuItemsMap.get(item.menuItemId);
          menuItemsMap.set(item.menuItemId, {
            id: item.menuItemId,
            name: item.menuItemName,
            quantity: item.quantity + menuItem.quantity,
            categoryName: item.categoryName,
            onPremiseCount: menuItem.onPremiseCount + (isSourceType0 ? 1 : 0),
            offPremiseCount: menuItem.offPremiseCount + (isSourceType0 ? 0 : 1),
          });
        } else {
          menuItemsMap.set(item.menuItemId, {
            id: item.menuItemId,
            name: item.menuItemName,
            quantity: item.quantity,
            categoryName: item.categoryName,
            onPremiseCount: isSourceType0 ? 1 : 0,
            offPremiseCount: isSourceType0 ? 0 : 1,
          });
        }
      });

      //payment by tender type
      payments.forEach(payment => {
        const tenderTypeName = payment.tenderTypeName;
        let paymentAmount = payment.paymentAmount || 0;
        let tipAmount = payment.tipAmount || 0;
        if (paymentByTenderType.has(payment.tenderTypeId)) {
          const existingPayment = paymentByTenderType.get(payment.tenderTypeId);
          paymentAmount += existingPayment.paymentAmount;
          tipAmount += existingPayment.tipAmount;
        }
        const tipPercent = (tipAmount && paymentAmount) ? (tipAmount / paymentAmount) : 0;
        paymentByTenderType.set(payment.tenderTypeId, {
          id: payment.tenderTypeId,
          tenderTypeName,
          paymentAmount,
          tipAmount,
          tipPercent,
        });
      });
    });

    let totalTipPercent = 0;
    if (suggestedTipIncludesTax) {
      totalTipPercent = (totalTipAmt && totalAmt) ? (totalTipAmt / totalAmt) : 0;
    } else {
      totalTipPercent = (totalTipAmt && subTotalAmt) ? (totalTipAmt / subTotalAmt) : 0;
    }
    const data = {
      totalCheck: allChecks.length,
      totalAmount: totalAmt,
      subTotalAmount: subTotalAmt,
      totalGuest: totalGuest,
      totalCategory: Array.from(categoriesMap.keys()).length,
      totalItem: Array.from(menuItemsMap.keys()).length,
      totalPaidAmount: totalPaidAmt,
      totalTipAmount: totalTipAmt,
      totalTipPercent: totalTipPercent,
      totalUnpaidAmount: totalUnpaidAmt,
      totalPaidCheck: totalPaidCheck,
      totalUnpaidCheck: allChecks.length - totalPaidCheck,
    };

    this.setState(prevState => ({
      commonData: {
        ...prevState.commonData,
        ...data,
      },
      paymentByTenderType,
      items: menuItemsMap,
      categories: categoriesMap,
    }));

    this.setRevenueCentersData(checksByRevenueCenter);
    this.setOrderTypesData(checksByOrderType);
    this.setHourlySalesData(checksByTimeSlot);
  };

  setRevenueCentersData = (checksByRevenueCenter) => {
    let revenueCentersData = new Map();
    checksByRevenueCenter.forEach((checkArray, rCenterId) => {
      const totalCheck = checkArray.length;
      const totalAmt = checkArray.reduce((total, eachCheck) => {
        return total + eachCheck.totalAmount;
      }, 0);
      const averageAmt = (totalCheck && totalAmt) ? totalAmt / totalCheck : 0;
      const rCenterName = this.revenueCentersMap.has(rCenterId) ? this.revenueCentersMap.get(rCenterId) : '';
      revenueCentersData.set(rCenterId, {
        id: rCenterId,
        orderTypeName: rCenterName,
        totalCheck: totalCheck,
        totalAmount: totalAmt,
        averageAmount: averageAmt,
      });
    });
    this.setState({ revenueCentersData });
  };

  setOrderTypesData = (checksByOrderType) => {
    let orderTypesData = new Map();
    checksByOrderType.forEach((checkArray, orderTypeId) => {
      const totalCheck = checkArray.length;
      const totalAmt = checkArray.reduce((total, eachCheck) => {
        return total + eachCheck.totalAmount;
      }, 0);
      const averageAmt = (totalCheck && totalAmt) ? totalAmt / totalCheck : 0;
      const orderTypeName = this.orderTypesMap.has(orderTypeId) ? this.orderTypesMap.get(orderTypeId) : '';
      orderTypesData.set(orderTypeId, {
        id: orderTypeId,
        orderTypeName: orderTypeName,
        totalCheck: totalCheck,
        totalAmount: totalAmt,
        averageAmount: averageAmt,
      });
    });
    this.setState({ orderTypesData });
  };

  setHourlySalesData = (checksByTimeSlot) => {
    const timeSlots = Array.from(checksByTimeSlot.keys());
    timeSlots.sort((a, b) => a - b);
    let hourlySales = new Map();
    if (checksByTimeSlot.size > 0 && timeSlots.length > 0) {
      const hoursSlot = range(timeSlots[0], timeSlots[timeSlots.length - 1]);
      hoursSlot.forEach(hour => {
        let totalCheck = 0, totalItem = 0, totalAmount = 0, averageAmount = 0, totalTipAmount = 0, onPremiseCount = 0, offPremiseCount = 0;
        if (checksByTimeSlot.has(hour)) {
          const checkArr = checksByTimeSlot.get(hour);
          totalCheck = checkArr.length;
          checkArr.forEach(eachCheck => {
            totalAmount += eachCheck.totalAmount;
            totalTipAmount += eachCheck.tipAmount;
            const items = eachCheck.items;
            items.forEach(eachItem => {
              totalItem += eachItem.quantity;
              onPremiseCount += (eachCheck.sourceType === 0 ? 1 : 0);
              offPremiseCount += (eachCheck.sourceType !== 0 ? 1 : 0);
            })
          });
          averageAmount = (totalAmount && totalCheck) ? totalAmount / totalCheck : 0;
        }
        hourlySales.set(hour, {
          id: hour,
          hour,
          totalCheck,
          totalItem,
          totalAmount,
          averageAmount,
          totalTipAmount,
          onPremiseCount,
          offPremiseCount,
        });
      });
    }
    this.setState({ hourlySales });
  };

  calculationForCheckSubscription = (newChecks) => {
    const allNewChecks = Array.from(newChecks.values());
    allNewChecks.forEach(eachCheck => {
      if (eachCheck.capiMessageType === 'new') {
        this.checks.set(eachCheck.id, eachCheck);
        const dataAfterAdded = this.calculateForNewCheck(eachCheck);
        this.setState({ ...dataAfterAdded });
      } else if (eachCheck.capiMessageType === 'updated') {
        const dataAfterDeletion = this.calculateForDeletedCheck(this.checks.get(eachCheck.id));
        this.setState({ ...dataAfterDeletion }, () => {
          this.checks.set(eachCheck.id, eachCheck);
          const dataAfterAdded = this.calculateForNewCheck(eachCheck);
          this.setState({ ...dataAfterAdded });
        });
      } else if (eachCheck.capiMessageType === 'deleted') {
        this.checks.delete(eachCheck.id);
        const dataAfterDeletion = this.calculateForDeletedCheck(eachCheck);
        this.setState({ ...dataAfterDeletion });
      }
    });

    const { context } = this.props;
    allNewChecks.forEach(check => context?.clearNewCheck(check.id));
  };

  calculateForNewCheck = (check) => {
    const { reduxProps: { storeInfo } } = this.props;
    const { suggestedTipIncludesTax } = storeInfo?.data?.tipInfo || {};
    let { items, categories, commonData, revenueCentersData, orderTypesData, hourlySales, paymentByTenderType } = this.state;
    let {
      totalCheck,
      totalAmount,
      subTotalAmount,
      totalPaidAmount,
      totalTipAmount,
      totalUnpaidAmount,
      totalPaidCheck,
      totalUnpaidCheck,
      totalGuest,
    } = commonData;
    let totalItem = 0;
    let onPremise = 0;
    let offPremise = 0;

    if (Array.isArray(check.items)) {
      check.items.forEach(item => {
        if (categories.has(item.categoryId)) {
          const categoryCount = categories.get(item.categoryId);
          categories.set(item.categoryId, categoryCount + 1);
        } else {
          categories.set(item.categoryId, 1);
        }

        if (items.has(item.menuItemId)) {
          const menuItem = items.get(item.menuItemId);
          items.set(item.menuItemId, {
            ...menuItem,
            quantity: item.quantity + menuItem.quantity,
            onPremiseCount: menuItem?.onPremiseCount + (check?.sourceType === 0 ? 1 : 0),
            offPremiseCount: menuItem?.offPremiseCount + (check?.sourceType !== 0 ? 1 : 0),
          });
        } else {
          items.set(item.menuItemId, {
            id: item.menuItemId,
            name: item.menuItemName,
            quantity: item.quantity,
            categoryName: item.categoryName,
            onPremiseCount: check?.sourceType === 0 ? 1 : 0,
            offPremiseCount: check?.sourceType !== 0 ? 1 : 0,
          });
        }

        totalItem += item.quantity;
        onPremise += (check?.sourceType === 0 ? 1 : 0);
        offPremise += (check?.sourceType !== 0 ? 1 : 0);
      });
    }

    //update payment by Tender Type
    if (Array.isArray(check.payments)) {
      check.payments.forEach(payment => {
        const tenderTypeName = payment.tenderTypeName;
        let paymentAmount = payment.paymentAmount || 0;
        let tipAmount = payment.tipAmount || 0;
        if (paymentByTenderType.has(payment.tenderTypeId)) {
          const existingPayment = paymentByTenderType.get(payment.tenderTypeId);
          paymentAmount += existingPayment.paymentAmount;
          tipAmount += existingPayment.tipAmount;
        }
        const tipPercent = (tipAmount && paymentAmount) ? (tipAmount / paymentAmount) : 0;
        paymentByTenderType.set(payment.tenderTypeId, {
          id: payment.tenderTypeId,
          tenderTypeName,
          paymentAmount,
          tipAmount,
          tipPercent,
        });
      });
    }

    //update revenue centers data
    let rCenterTotalAmt = check.totalAmount;
    let rCenterTotalCheck = 1;
    const rCenterName = this.revenueCentersMap.has(check.revenueCenterId) ? this.revenueCentersMap.get(check.revenueCenterId) : '';
    if (revenueCentersData.has(check.revenueCenterId)) {
      const rCenterCheck = revenueCentersData.get(check.revenueCenterId);
      rCenterTotalAmt += rCenterCheck.totalAmount;
      rCenterTotalCheck += rCenterCheck.totalCheck;
    }
    const rCenterAvgAmt = (rCenterTotalCheck && rCenterTotalAmt) ? rCenterTotalAmt / rCenterTotalCheck : 0;
    revenueCentersData.set(check.revenueCenterId, {
      id: check.revenueCenterId,
      orderTypeName: rCenterName,
      totalCheck: rCenterTotalCheck,
      totalAmount: rCenterTotalAmt,
      averageAmount: rCenterAvgAmt,
    });

    //update order types data
    let odrTypeTotalAmt = check.totalAmount;
    let odrTypeTotalCheck = 1;
    const odrTypeName = this.orderTypesMap.has(check.orderTypeId) ? this.orderTypesMap.get(check.orderTypeId) : '';
    if (orderTypesData.has(check.orderTypeId)) {
      const odrTypeCheck = orderTypesData.get(check.orderTypeId);
      odrTypeTotalAmt += odrTypeCheck.totalAmount;
      odrTypeTotalCheck += odrTypeCheck.totalCheck;
    }
    const odrTypeAvgAmt = (odrTypeTotalCheck && odrTypeTotalAmt) ? odrTypeTotalAmt / odrTypeTotalCheck : 0;
    orderTypesData.set(check.orderTypeId, {
      id: check.orderTypeId,
      orderTypeName: odrTypeName,
      totalCheck: odrTypeTotalCheck,
      totalAmount: odrTypeTotalAmt,
      averageAmount: odrTypeAvgAmt,
    });

    //update hourly data
    const { hours } = getHoursAndMins(check.openDate);
    const hourlySale = hourlySales.get(hours);
    const hourlySaleTotalAmt = hourlySale.totalAmount + check.totalAmount;
    const hourlySaleTotalCheck = hourlySale.totalCheck + 1;
    const hourlySaleAvgAmt = (hourlySaleTotalAmt && hourlySaleTotalCheck) ? hourlySaleTotalAmt / hourlySaleTotalCheck : 0;
    hourlySales.set(hours, {
      ...hourlySale,
      totalCheck: hourlySaleTotalCheck,
      totalItem: hourlySale.totalItem + totalItem,
      totalAmount: hourlySaleTotalAmt,
      averageAmount: hourlySaleAvgAmt,
      totalTipAmount: hourlySale.totalTipAmount + check.tipAmount,
      onPremiseCount: hourlySale.onPremiseCount + onPremise,
      offPremiseCount: hourlySale.offPremiseCount + offPremise,
    });

    let totalTipPercent = 0;
    const totalTipAmt = totalTipAmount + check.tipAmount;
    const subTotalAmt = subTotalAmount + check.subTotalAmount;
    const totalAmt = totalAmount + check.totalAmount;
    if (suggestedTipIncludesTax) {
      totalTipPercent = (totalTipAmt && totalAmt) ? (totalTipAmt / totalAmt) : 0;
    } else {
      totalTipPercent = (totalTipAmt && subTotalAmt) ? (totalTipAmt / subTotalAmt) : 0;
    }

    return {
      items,
      categories,
      orderTypesData,
      revenueCentersData,
      hourlySales,
      paymentByTenderType,
      commonData: {
        totalCheck: totalCheck + 1,
        totalAmount: totalAmt,
        subTotalAmount: subTotalAmt,
        totalTipAmount: totalTipAmt,
        totalTipPercent: totalTipPercent,
        totalPaidAmount: totalPaidAmount + check.paidAmount,
        totalUnpaidAmount: totalUnpaidAmount + check.unpaidAmount,
        totalPaidCheck: totalPaidCheck + (check.totalAmount > 0 && check.unpaidAmount === 0) ? 1 : 0,
        totalUnpaidCheck: totalUnpaidCheck + (check.totalAmount > 0 && check.unpaidAmount === 0) ? 0 : 1,
        totalGuest: totalGuest + check.guestCount,
        totalCategory: Array.from(categories.keys()).length,
        totalItem: Array.from(items.keys()).length,
      },
    };
  };

  calculateForDeletedCheck = (deletedCheck) => {
    const { reduxProps: { storeInfo } } = this.props;
    const { suggestedTipIncludesTax } = storeInfo?.data?.tipInfo || {};
    let { items, categories, commonData, revenueCentersData, orderTypesData, hourlySales, paymentByTenderType } = this.state;
    let {
      totalCheck,
      totalAmount,
      subTotalAmount,
      totalPaidAmount,
      totalTipAmount,
      totalUnpaidAmount,
      totalPaidCheck,
      totalGuest,
    } = commonData;
    let deletedTotalItem = 0;
    let onPremise = 0;
    let offPremise = 0;

    if (Array.isArray(deletedCheck.items)) {
      deletedCheck.items.forEach(item => {
        const categoryCount = categories.get(item.categoryId);
        if (categoryCount === 1) {
          categories.delete(item.categoryId);
        } else {
          categories.set(item.categoryId, categoryCount - 1);
        }

        const menuItem = items.get(item.menuItemId);
        if (menuItem.quantity === 1) {
          items.delete(item.menuItemId)
        } else {
          items.set(item.menuItemId, {
            ...menuItem,
            quantity: menuItem.quantity - 1,
            onPremiseCount: menuItem.onPremiseCount - (deletedCheck?.sourceType === 0 ? 1 : 0),
            offPremiseCount: menuItem.offPremiseCount - (deletedCheck?.sourceType !== 0 ? 1 : 0),
          });
        }
        deletedTotalItem += item.quantity;
        onPremise += (deletedCheck?.sourceType === 0 ? 1 : 0);
        offPremise += (deletedCheck?.sourceType !== 0 ? 1 : 0);
      });
    }

    //update payment by Tender Type
    if (Array.isArray(deletedCheck.payments)) {
      deletedCheck.payments.forEach(payment => {
        const existingPayTender = paymentByTenderType.get(payment.tenderTypeId);
        if (existingPayTender.paymentAmount - payment.paymentAmount === 0) {
          paymentByTenderType.delete(payment.tenderTypeId);
        } else {
          const tenderTipAmt = existingPayTender.tipAmount - payment.tipAmount;
          const tenderPaymentAmt = existingPayTender.paymentAmount - payment.paymentAmount;
          const tenderTipPercent = (tenderTipAmt && tenderPaymentAmt) ? (tenderTipAmt / tenderPaymentAmt) : 0;
          paymentByTenderType.set(existingPayTender.tenderTypeId, {
            ...existingPayTender,
            paymentAmount: tenderPaymentAmt,
            tipAmount: tenderTipAmt,
            tipPercent: tenderTipPercent,
          });
        }
      });
    }

    //update revenue centers data
    const rCenterCheck = revenueCentersData.get(deletedCheck.revenueCenterId);
    if (rCenterCheck?.totalCheck === 1) {
      revenueCentersData.delete(deletedCheck.revenueCenterId);
    } else {
      const rCenterTotalAmt = rCenterCheck.totalAmount - deletedCheck.totalAmount;
      const rCenterAvgAmt = rCenterTotalAmt / (rCenterCheck.totalCheck - 1);
      revenueCentersData.set(rCenterCheck.id, {
        ...rCenterCheck,
        totalCheck: rCenterCheck.totalCheck - 1,
        totalAmount: rCenterTotalAmt,
        averageAmount: rCenterAvgAmt,
      });
    }

    //update order types data
    const odrTypeCheck = orderTypesData.get(deletedCheck.orderTypeId);
    if (odrTypeCheck?.totalCheck === 1) {
      orderTypesData.delete(deletedCheck.orderTypeId);
    } else {
      const odrTypeTotalAmt = odrTypeCheck.totalAmount - deletedCheck.totalAmount;
      const odrTypeAvgAmt = odrTypeTotalAmt / (odrTypeCheck.totalCheck - 1);
      orderTypesData.set(odrTypeCheck.id, {
        ...odrTypeCheck,
        totalCheck: odrTypeCheck.totalCheck - 1,
        totalAmount: odrTypeTotalAmt,
        averageAmount: odrTypeAvgAmt,
      });
    }

    //update hourly data
    const { hours } = getHoursAndMins(deletedCheck.openDate);
    const hourlySale = hourlySales.get(hours);
    const hourlySaleTotalAmt = hourlySale.totalAmount - deletedCheck.totalAmount;
    const hourlySaleTotalCheck = hourlySale.totalCheck - 1;
    const hourlySaleAvgAmt = (hourlySaleTotalAmt && hourlySaleTotalCheck) ? hourlySaleTotalAmt / hourlySaleTotalCheck : 0;
    hourlySales.set(hours, {
      ...hourlySale,
      totalCheck: hourlySaleTotalCheck,
      totalItem: hourlySale.totalItem - deletedTotalItem,
      totalAmount: hourlySaleTotalAmt,
      averageAmount: hourlySaleAvgAmt,
      totalTipAmount: hourlySale.totalTipAmount - deletedCheck.tipAmount,
      onPremiseCount: hourlySale.onPremiseCount - onPremise,
      offPremiseCount: hourlySale.offPremiseCount - offPremise,
    });

    let totalTipPercent = 0;
    const totalTipAmt = totalTipAmount - deletedCheck.tipAmount;
    const subTotalAmt = subTotalAmount - deletedCheck.subTotalAmount;
    const totalAmt = totalAmount - deletedCheck.totalAmount;
    if (suggestedTipIncludesTax) {
      totalTipPercent = (totalTipAmt && totalAmt) ? (totalTipAmt / totalAmt) : 0;
    } else {
      totalTipPercent = (totalTipAmt && subTotalAmt) ? (totalTipAmt / subTotalAmt) : 0;
    }
    const totalUnpaidAmt = (totalUnpaidAmount - deletedCheck.unpaidAmount) > 0 ? (totalUnpaidAmount - deletedCheck.unpaidAmount) : 0;
    const totalPaidCheckCount = totalPaidCheck - (deletedCheck.totalAmount > 0 && deletedCheck.unpaidAmount === 0) ? 1 : 0;
    const totalCheckCount = totalCheck - 1;

    return {
      items,
      categories,
      orderTypesData,
      revenueCentersData,
      hourlySales,
      paymentByTenderType,
      commonData: {
        totalCheck: totalCheckCount,
        totalAmount: totalAmt,
        subTotalAmount: subTotalAmt,
        totalTipAmount: totalTipAmt,
        totalTipPercent: totalTipPercent,
        totalPaidAmount: totalPaidAmount - deletedCheck.paidAmount,
        totalUnpaidAmount: totalUnpaidAmt,
        totalPaidCheck: totalPaidCheckCount,
        totalUnpaidCheck: totalCheckCount - totalPaidCheckCount,
        totalGuest: totalGuest - deletedCheck.guestCount,
        totalCategory: Array.from(categories.keys()).length,
        totalItem: Array.from(items.keys()).length,
      },
    };
  };

  get viewProps() {
    const {
      popup,
      items,
      commonData,
      hourlySales,
      paymentByTenderType,
      revenueCentersData,
      orderTypesData,
    } = this.state;

    return {
      popup,
      items,
      commonData,
      hourlySales,
      paymentByTenderType,
      revenueCentersData,
      orderTypesData,
    };
  }

  render() {
    const viewProps = this.viewProps;
    return (
      <View  {...viewProps} />
    );
  }
}

const mapStateToProps = (reduxProps) => ({
  reduxProps,
});

export default connect(mapStateToProps, null)(withSocketContext(Dashboard));
